import {
  type QueryKey,
  type UseSuspenseQueryOptions,
  useMutation,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { toolsKeys } from "@/app/tools/api/query-keys";
import type { ScopedBinding } from "@/app/types";
import { getEngineApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";
import type { CreateSecret, Secret } from "../types";
import { createSecret } from "./create-secret";
import { deleteSecret } from "./delete-secret";
import { secretsQueryOptions } from "./get-secrets";
import { secretsKeys } from "./query-keys";
import { type UpdateSecretRequest, updateSecret } from "./update-secret";

// IMPORTANT: Query hooks must read route scope so org/project changes re-key queries.

export const useGetSecrets = <TData = Secret[]>(
  binding: ScopedBinding = "project",
  options?: Omit<
    UseSuspenseQueryOptions<Secret[], Error, TData, QueryKey>,
    "queryKey" | "queryFn"
  >
) => {
  const scope = useResourceScope();

  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  return useSuspenseQuery<Secret[], Error, TData, QueryKey>({
    ...secretsQueryOptions({ binding, scope }),
    ...options,
  } as UseSuspenseQueryOptions<Secret[], Error, TData, QueryKey>);
};

export const useCreateSecret = () => {
  const scope = useResourceScope();

  return useMutation({
    mutationFn: (secret: CreateSecret) =>
      createSecret({
        client: getEngineApi(scope, secret.binding.type),
        secret,
      }),
    meta: {
      invalidates: [secretsKeys.lists({ scope }), toolsKeys.lists({ scope })],
      errorMessage: "Unable to save secret - please try again",
    },
    onSuccess: () => {
      toast.success("Secret created successfully");
    },
  });
};

export const useUpdateSecret = ({
  id,
  binding,
}: {
  id: string;
  binding?: ScopedBinding;
}) => {
  const resolvedBinding = binding || "project";
  const scope = useResourceScope();
  const client = getEngineApi(scope, resolvedBinding);

  return useMutation({
    mutationFn: (secret: UpdateSecretRequest) =>
      updateSecret({ client, id, secret }),
    meta: {
      invalidates: [secretsKeys.lists({ scope }), toolsKeys.lists({ scope })],
      errorMessage: "Unable to update secret - please try again",
    },
    onSuccess: () => {
      toast.success("Secret updated successfully");
    },
  });
};

export const useDeleteSecret = () => {
  const scope = useResourceScope();

  return useMutation({
    mutationFn: ({ binding, id }: { binding: ScopedBinding; id: string }) =>
      deleteSecret({
        client: getEngineApi(scope, binding),
        id,
      }),
    meta: {
      invalidates: [secretsKeys.lists({ scope }), toolsKeys.lists({ scope })],
      errorMessage: "Unable to delete secret - please try again",
    },
    onSuccess: () => {
      toast.success("Secret deleted successfully");
    },
  });
};
